import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { PageHeader } from '../../components/layout/PageHeader';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { ApiError } from '../../lib/api';
import type { CloneGoalWire, CloneRequestBody, CloneResponse } from '../../types/metaLiveCampaigns';
import { cloneCampaign, getLeadForms, getLiveCampaignDetail } from './api';
import { useDebouncedValue } from './useDebouncedValue';

// Clone to a new campaign: read a live campaign (read-only), show how the builder would
// represent it, and open the result as an ordinary draft. The source is never modified and
// the copy's first publish is always PAUSED. A campaign the builder cannot represent is
// refused here with the reasons; nothing is dropped or merged silently.

const GOALS: Array<{ value: CloneGoalWire; label: string; hint: string }> = [
  { value: 'keep', label: 'Keep the source goal', hint: 'Same destination and optimisation as the source.' },
  { value: 'instant_form', label: 'Change to an instant form', hint: 'Lead generation with a lead form from this account\'s Page.' },
  { value: 'website', label: 'Change to a website', hint: "Website conversions with this account's pixel and the LEAD event." },
];

export function CloneCampaignPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const accountId = params.get('account') ?? '';
  const campaignId = params.get('campaign') ?? '';

  const [goal, setGoal] = useState<CloneGoalWire>('keep');
  const [targetingMode, setTargetingMode] = useState<'keep' | 'edit'>('keep');
  const [name, setName] = useState('');
  const [leadFormId, setLeadFormId] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [onlyAdSetId, setOnlyAdSetId] = useState<string | null>(null);

  const source = useQuery({
    queryKey: ['metaLiveCampaign', accountId, campaignId],
    queryFn: () => getLiveCampaignDetail(accountId, campaignId),
    enabled: Boolean(accountId && campaignId),
  });
  const forms = useQuery({
    queryKey: ['metaAdLeadForms', accountId],
    queryFn: () => getLeadForms(accountId),
    enabled: Boolean(accountId) && goal === 'instant_form',
  });

  // The plan re-reads when a CHOICE changes. The name does not affect it, and the link is
  // debounced so typing does not re-read the campaign on every keystroke.
  const debouncedUrl = useDebouncedValue(websiteUrl.trim(), 600);
  const body = useMemo<CloneRequestBody>(
    () => ({
      name: null,
      goal,
      targetingMode,
      onlyAdSetId,
      leadFormId: leadFormId || null,
      websiteUrl: debouncedUrl || null,
      dryRun: true,
    }),
    [goal, targetingMode, onlyAdSetId, leadFormId, debouncedUrl],
  );

  const [preview, setPreview] = useState<CloneResponse | null>(null);
  const plan = useMutation({
    mutationFn: (b: CloneRequestBody) => cloneCampaign(accountId, campaignId, b),
    onSuccess: setPreview,
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Could not read the campaign.'),
  });
  // The plan is read when a choice changes (a deliberate click or edit), never on a timer.
  useEffect(() => {
    if (accountId && campaignId) plan.mutate(body);
  }, [body, accountId, campaignId]); // eslint-disable-line react-hooks/exhaustive-deps

  const create = useMutation({
    mutationFn: () => cloneCampaign(accountId, campaignId, { ...body, name: name.trim() || null, websiteUrl: websiteUrl.trim() || null, dryRun: false }),
    onSuccess: (r) => {
      if (r.ok && r.draft) navigate(`/advertising/meta/compose/${r.draft.id}`);
      else setPreview(r);
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Could not create the draft.'),
  });

  if (!accountId || !campaignId) {
    return (
      <div>
        <PageHeader title="Clone to a new campaign" backHref="/advertising/meta/add-to-live" />
        <p className="text-sm text-slate-600">
          Start from the campaign list: <Link className="underline" to="/advertising/meta/add-to-live">pick a campaign</Link> and choose "Clone to a new campaign".
        </p>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Clone to a new campaign"
        description="Copy a live campaign into a draft, optionally changing its goal. The source campaign is never modified, and the copy publishes PAUSED."
        backHref="/advertising/meta/add-to-live"
      />
      {source.isLoading && <CenteredSpinner />}
      {source.data && (
        <div className="max-w-4xl space-y-8">
          <h2 className="text-base font-semibold text-slate-900">
            Source: {source.data.name} <Badge variant={source.data.isLive ? 'success' : 'neutral'}>{source.data.isLive ? 'Live' : (source.data.metaStatus ?? 'Unknown')}</Badge>
          </h2>

          <section aria-label="Name" className="max-w-md">
            <label className="mb-1.5 block text-sm font-medium text-slate-700" htmlFor="clone-name">
              New campaign name
            </label>
            <input
              id="clone-name"
              value={name}
              placeholder={`${source.data.name} – copy`}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            />
          </section>

          <section aria-label="Goal" className="space-y-2">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Goal</h3>
            <div role="radiogroup" aria-label="Goal" className="space-y-2">
              {GOALS.map((g) => (
                <label key={g.value} className="flex items-start gap-2 text-sm">
                  <input type="radio" name="clone-goal" checked={goal === g.value} onChange={() => setGoal(g.value)} className="mt-1" />
                  <span>
                    <span className="font-medium">{g.label}</span>
                    <span className="block text-xs text-slate-500">{g.hint}</span>
                  </span>
                </label>
              ))}
            </div>
            {goal === 'instant_form' && (
              <label className="flex max-w-md items-center gap-2 text-sm">
                Lead form
                <select aria-label="Lead form" value={leadFormId} onChange={(e) => setLeadFormId(e.target.value)} className="flex-1 rounded-md border border-slate-300 px-2 py-1.5">
                  <option value="">Choose a form…</option>
                  {(forms.data?.leadForms ?? []).map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {goal === 'website' && (
              <label className="flex max-w-lg items-center gap-2 text-sm">
                Website link
                <input
                  aria-label="Website link"
                  value={websiteUrl}
                  placeholder="https://…"
                  onChange={(e) => setWebsiteUrl(e.target.value)}
                  className="flex-1 rounded-md border border-slate-300 px-2 py-1.5"
                />
              </label>
            )}
          </section>

          <section aria-label="Targeting" className="space-y-2">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Targeting</h3>
            <div role="radiogroup" aria-label="Targeting" className="space-y-2">
              <label className="flex items-start gap-2 text-sm">
                <input type="radio" name="clone-targeting" checked={targetingMode === 'keep'} onChange={() => setTargetingMode('keep')} className="mt-1" />
                <span>
                  <span className="font-medium">Keep the source targeting exactly</span>
                  <span className="block text-xs text-slate-500">Sent as it is, including anything the builder cannot show. Read-only in the builder.</span>
                </span>
              </label>
              <label className="flex items-start gap-2 text-sm">
                <input type="radio" name="clone-targeting" checked={targetingMode === 'edit'} onChange={() => setTargetingMode('edit')} className="mt-1" />
                <span>
                  <span className="font-medium">Edit targeting</span>
                  <span className="block text-xs text-slate-500">Convert it into the builder's audience model so you can change it. Anything the model cannot express is listed below and will be dropped.</span>
                </span>
              </label>
            </div>
            {targetingMode === 'edit' && preview?.ok && (
              <ul className="space-y-1 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="clone-drops">
                {preview.summary.targetingDrops.map((d) => (
                  <li key={d.adSetName}>
                    <strong>{d.adSetName}:</strong> {d.dropped.length === 0 ? 'nothing will be dropped.' : `will be dropped: ${d.dropped.join(', ')}.`}
                  </li>
                ))}
              </ul>
            )}
          </section>

          {plan.isPending && !preview && <CenteredSpinner />}

          {preview && !preview.ok && (
            <section aria-label="Cannot clone" className="space-y-2 rounded-md border border-red-200 bg-red-50 p-4" data-testid="clone-refusal">
              <h3 className="text-sm font-semibold text-red-800">This campaign cannot be cloned as it is</h3>
              <ul className="list-disc space-y-1 pl-5 text-sm text-red-800">
                {preview.reasons.map((r) => (
                  <li key={r}>{r}</li>
                ))}
              </ul>
              {preview.oneAdSetPossible && (
                <div className="space-y-1">
                  <p className="text-sm text-slate-700">Or clone just one ad set:</p>
                  <div className="flex flex-wrap gap-2">
                    {source.data.adSets.map((s) => (
                      <Button key={s.id} variant="outline" size="sm" onClick={() => setOnlyAdSetId(s.id)}>
                        {s.name || s.id}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </section>
          )}

          {preview?.ok && (
            <section aria-label="What will be cloned" className="space-y-3">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">What will be cloned</h3>
              {onlyAdSetId && (
                <p className="text-xs text-slate-600">
                  One ad set only. <button type="button" className="underline" onClick={() => setOnlyAdSetId(null)}>Clone the whole campaign instead</button>
                </p>
              )}
              <p className="text-sm text-slate-800">
                {preview.summary.objective === 'LEADS' ? 'Leads' : 'Traffic'} campaign, {preview.summary.budgetMode === 'CBO' ? 'campaign budget' : 'ad set budgets'}, {preview.summary.adSets.length} ad set{preview.summary.adSets.length === 1 ? '' : 's'}, {preview.summary.ads.length} ad{preview.summary.ads.length === 1 ? '' : 's'} per ad set.
              </p>
              <ul className="list-disc space-y-0.5 pl-5 text-sm text-slate-700">
                {preview.summary.adSets.map((s) => (
                  <li key={s.sourceId}>
                    {s.name || s.sourceId} ({s.adCount} ad{s.adCount === 1 ? '' : 's'})
                  </li>
                ))}
              </ul>
              <ul className="list-disc space-y-0.5 pl-5 text-sm text-slate-700">
                {preview.summary.ads.map((a) => (
                  <li key={a.name}>
                    {a.name} · {a.media}
                    {a.texts > 1 ? ` · ${a.texts} text options` : ''}
                  </li>
                ))}
              </ul>
              <ul className="space-y-1 text-xs text-slate-600" data-testid="clone-notes">
                {preview.notes.map((n) => (
                  <li key={n}>{n}</li>
                ))}
              </ul>
              <Button onClick={() => create.mutate()} disabled={create.isPending || plan.isPending}>
                {create.isPending ? 'Creating the draft…' : 'Create the draft'}
              </Button>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
